/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@atlaskit/css';

import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import IconButton from '@atlaskit/button/icon/button';

import LinkButton from '@atlaskit/button/link';

import { cssMap } from '@atlaskit/css';

import Heading from '@atlaskit/heading/heading';

import CrossIcon from '@atlaskit/icon/core/cross';

import Image from '@atlaskit/image';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { token } from '@atlaskit/tokens';

import { AdControls } from '@post-office/components--ad-controls';

import { type NextBestAction } from '@post-office/confluence-next-best-action';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { PlacementFrontend } from '@post-office/browser--growth/confluence-side-nav';

import { FormattedMessage, useIntl } from 'react-intl';

import NotesImage from './images/notes.png';

import { registeredContent } from './content';

import { type MessageTemplateContext } from '../../..';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

const whyAmISeeingThisModalContent = {
	title: <FormattedMessage {...registeredContent.default.whyAmISeeingThisTitle} />,
	message: (
		<p>
			<FormattedMessage {...registeredContent.default.whyAmISeeingThisMessage} />
		</p>
	),
	reasons: [
		<FormattedMessage key="actions" {...registeredContent.default.whyAmISeeingThisReasonActions} />,
		<FormattedMessage
			key="suggestions"
			{...registeredContent.default.whyAmISeeingThisReasonSuggestions}
		/>,
	],
};

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		padding: token('space.150'),
		width: '100%',
	},
	body: {
		marginTop: token('space.100'),
	},
	ctaWrapper: {
		marginTop: token('space.150'),
	},
});

type MessageFrontendProps = MessageTemplateContext & {
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,

		Component: ({
			nextBestActionType,
			handleOnDismissed,
			onSideNavClick,
		}: MessageFrontendProps) => {
			const { onClosed, onCtaActioned, onDeclined } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();

			const intl = useIntl();
			const headerText = intl.formatMessage(registeredContent.default.header);
			const bodyText = intl.formatMessage(registeredContent.default.body);
			const ctaText = intl.formatMessage(registeredContent.default.cta);
			const closeLabel = intl.formatMessage(registeredContent.default.close);
			const imageAltText = intl.formatMessage(registeredContent.default.imageAlt);

			const handleDismiss = () => {
				// AdControls' "Not interested" menu item — an explicit refusal, not a plain close.
				onDeclined({ interactionIdentifier: 'dismiss' });
				void messageDismissed();
				void handleOnDismissed();
			};

			const handleCloseClicked = () => {
				onClosed({ interactionIdentifier: '1on1-loom-close' });
				void messageDismissed();
				void onSideNavClick(nextBestActionType);
			};

			const handleCtaClicked = () => {
				onCtaActioned({ interactionIdentifier: '1on1-loom-cta' });
				void messageDismissed();
				void onSideNavClick(nextBestActionType);
			};

			return (
				<AnalyticsContext data={{ source: 'confluence-side-nav-1-on-1-loom-nudge-message' }}>
					<div css={styles.container}>
						<Inline spread="space-between">
							<Image src={NotesImage} alt={imageAltText} height="56" width="56" />
							<Inline>
								<AdControls
									iconSize="small"
									showWhyAmISeeingThis={true}
									whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
									onDismissed={handleDismiss}
								/>
								<IconButton
									onClick={handleCloseClicked}
									appearance="subtle"
									icon={CrossIcon}
									label={closeLabel}
								/>
							</Inline>
						</Inline>
						<Heading size="small">{headerText}</Heading>
						<div css={styles.body}>{bodyText}</div>
						<div css={styles.ctaWrapper}>
							<LinkButton
								onClick={handleCtaClicked}
								href="https://www.loom.com/welcome"
								spacing="compact"
								target="_blank"
							>
								{ctaText}
							</LinkButton>
						</div>
					</div>
				</AnalyticsContext>
			);
		},
	},
});
